import { useMemo, useState, useEffect, useRef } from 'react';
import { addAction, addError } from '@shared/utils/logger';
import { isValidProperty, transformPropertiesToListings } from '../AccommodationsConsole.utils';
import { isValidCoordinates } from '@shared/core/places';
import {
  AccommodationCatalogUnitsOccupancyInput,
  FetchStrategy,
  SearchExtendedAccommodationPropertiesV2Payload,
  useAccommodationsConsolePropertiesV2Query,
} from '@graphql/generated';
import { AccommodationsConsoleState, JoyHotelAccommodationListingDTO } from '@apps/accommodations/AccommodationsConsole.types';
import { useExtendedAccommodationSearchMetadata } from '@shared/hooks/useSearchAccommodationsQueryMetadataPayload';
import { useStandardHotelRoomPriceMapping } from './useListingPriceMapping';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import { isEqual } from 'lodash-es';

interface UseAccommodationsConsoleListingsArgs
  extends Readonly<{
    isInitialLoad: boolean;
    shouldPrioritizeParams?: boolean;
    state: AccommodationsConsoleState;
  }> {}

export type ConfirmedSearchPayload = Readonly<{
  location: Readonly<{
    latitude: number;
    longitude: number;
    context: {
      googlePlaceId: string | undefined;
    };
  }> | null;
  searchDates: Readonly<{
    checkin: string;
    checkout: string;
  }>;
  occupancies: AccommodationCatalogUnitsOccupancyInput;
}> | null;

/**
 * Determines if custom search params should be skipped.
 * When true, the query uses only eventHandle (shows event venue first).
 * When false, the query includes custom search params (location, dates, occupancies).
 */
const shouldSkipCustomSearchParams = (isInitialLoad: boolean, shouldPrioritizeParams: boolean, hasValidVenueLocation: boolean): boolean => {
  return isInitialLoad && !shouldPrioritizeParams && hasValidVenueLocation;
};

export const useRegionalHotels = (args: UseAccommodationsConsoleListingsArgs) => {
  const { state, isInitialLoad, shouldPrioritizeParams = false } = args;
  const { eventHandle } = useEventInfo();
  const currencyCode = useTravelWhiteLabelCurrency();

  const [cachedListings, setCachedListings] = useState<Array<JoyHotelAccommodationListingDTO>>([]);
  const [confirmedSearchPayload, setConfirmedSearchPayload] = useState<ConfirmedSearchPayload>(null);

  const { isPreparingSearchMetadata, searchMetadata } = useExtendedAccommodationSearchMetadata();

  const hasValidLocationSearchCoordinates = isValidCoordinates(state.circularRegionSearch);

  const locationPayload = useMemo(() => {
    if (!hasValidLocationSearchCoordinates) {
      return null;
    }
    return {
      latitude: state.circularRegionSearch?.latitude,
      longitude: state.circularRegionSearch?.longitude,
      context: {
        googlePlaceId: state.circularRegionSearch?.googlePlaceId,
      },
    };
  }, [hasValidLocationSearchCoordinates, state.circularRegionSearch?.latitude, state.circularRegionSearch?.longitude, state.circularRegionSearch?.googlePlaceId]);

  const hasValidVenueLocation = !!state.venue.location && isValidCoordinates({ latitude: state.venue.location.lat, longitude: state.venue.location.lng });

  const skipCustomSearchParams = shouldSkipCustomSearchParams(isInitialLoad, shouldPrioritizeParams, hasValidVenueLocation);

  const mapHotelRoomPrice = useStandardHotelRoomPriceMapping();

  // Build payload conditionally - null when we can't build a valid payload
  const rawPayload = useMemo(() => {
    if (!eventHandle) {
      return null;
    }

    // When skipping custom search params, only include eventHandle (shows event venue first)
    if (skipCustomSearchParams) {
      return {
        eventHandle,
        customSearchParams: {
          useRealtimePricing: false,
          fetchStrategy: FetchStrategy.PREFER_CACHE,
        },
      };
    }

    // When including custom search params, require valid location coordinates
    if (!hasValidLocationSearchCoordinates || !locationPayload || isPreparingSearchMetadata) {
      return null;
    }

    return {
      eventHandle,
      customSearchParams: {
        currencyCode,
        radiusKM: 2,
        location: locationPayload,
        useRealtimePricing: false,
        fetchStrategy: FetchStrategy.PREFER_CACHE,
        stayDates: {
          checkIn: {
            high: state.searchDates.checkin,
            low: state.searchDates.checkin,
          },
          checkOut: {
            high: state.searchDates.checkout,
            low: state.searchDates.checkout,
          },
        },
        occupancies: state.occupancies,
        metadata: searchMetadata,
      },
    };
  }, [
    eventHandle,
    skipCustomSearchParams,
    hasValidLocationSearchCoordinates,
    currencyCode,
    locationPayload,
    isPreparingSearchMetadata,
    state.searchDates.checkin,
    state.searchDates.checkout,
    state.occupancies,
    searchMetadata,
  ]);

  const payloadRef = useRef(rawPayload);

  if (!isEqual(payloadRef.current, rawPayload)) {
    payloadRef.current = rawPayload;
  }

  const payload = payloadRef.current;

  useEffect(() => {
    setConfirmedSearchPayload(null);
    setCachedListings([]);
  }, [payload]);

  const {
    data,
    loading: isFetchingListings,
    error,
  } = useAccommodationsConsolePropertiesV2Query({
    variables: {
      payload: payload!,
    },
    skip: !payload,
    onCompleted: data => {
      if (data?.searchExtendedAccommodationProperties?.properties) {
        const validProperties = data.searchExtendedAccommodationProperties.properties.filter(isValidProperty);
        const tempCachedListings = transformPropertiesToListings(validProperties, mapHotelRoomPrice);
        setCachedListings(tempCachedListings);
        const customParams = payload?.customSearchParams;
        const newConfirmedSearchPayload =
          customParams?.stayDates && customParams?.occupancies
            ? {
                occupancies: customParams.occupancies,
                location: customParams.location ?? null,
                searchDates: {
                  checkin: customParams.stayDates.checkIn.low,
                  checkout: customParams.stayDates.checkOut.low,
                },
              }
            : null;
        setConfirmedSearchPayload(newConfirmedSearchPayload);
      }

      if (!data?.searchExtendedAccommodationProperties?.properties?.length) {
        addAction('Search extended accommodations properties with circularRegionSearch api with empty results (AccommodationsConsole)', {
          context: 'searchExtendedAccommodationProperties',
          circularRegionSearch: state.circularRegionSearch,
        });
      }
    },
    onError: error => {
      addError(error, {
        source: 'Search extended accommodations properties with circularRegionSearch api failed (AccommodationsConsole)',
        context: 'searchExtendedAccommodationProperties',
        errorMessage: error.message,
        circularRegionSearch: state.circularRegionSearch,
      });
    },
    batchMode: 'off',
  });

  const listings = useMemo((): readonly JoyHotelAccommodationListingDTO[] => {
    if (!data?.searchExtendedAccommodationProperties?.properties) {
      return [];
    }
    const validProperties = data.searchExtendedAccommodationProperties.properties.filter(isValidProperty);
    return transformPropertiesToListings(validProperties, mapHotelRoomPrice);
  }, [data, mapHotelRoomPrice]);

  return {
    /**
     * Can be used to retain maps points while loading new data
     */
    cachedListings,
    listings,
    isFetchingListings,
    error,
    confirmedSearchPayload,
    payload: payload as SearchExtendedAccommodationPropertiesV2Payload,
  };
};
